import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";

const topicColors = {
  "Tuberculosis":    "#8A1613",
  "HIV/AIDS":        "#007965",
  "Diabetes":        "#B07D1A",
  "Mental Health":   "#534AB7",
  "Maternal Health": "#8A1613",
  "Men's Health":    "#007965",
};

export default function Podcast() {
  const [episodes, setEpisodes]   = useState([]);
  const [topics, setTopics]       = useState([]);
  const [filter, setFilter]       = useState("All");
  const [playing, setPlaying]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const audioRef = useRef(null);

  useEffect(() => {
    Promise.all([api.getEpisodes(), api.getTopics()])
      .then(([eps, t]) => { setEpisodes(eps); setTopics(["All", ...t]); })
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "All" ? episodes : episodes.filter(e => e.topic === filter);

  const handlePlay = (ep) => {
    if (playing?.id === ep.id) { audioRef.current?.pause(); setPlaying(null); }
    else setPlaying(ep);
  };

  useEffect(() => {
    if (playing && audioRef.current) { audioRef.current.load(); audioRef.current.play().catch(()=>{}); }
  }, [playing]);

  return (
    <div style={{ paddingTop: "5rem" }}>
      {/* Hero */}
      <section style={{ background: "#0B0B0B", padding: "6rem 2rem 5rem", position: "relative", overflow: "hidden", minHeight: "50vh", display: "flex", alignItems: "center" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(138,22,19,0.15) 0%, transparent 70%)" }} />
          {["HEALTH","STORIES","VOICES","PODCAST"].map((w,i) => (
            <span key={i} style={{ position: "absolute", fontFamily: "var(--font)", fontWeight: 900, fontSize: "clamp(5rem,12vw,14rem)", color: "transparent", WebkitTextStroke: "1px rgba(149,187,131,0.06)", top: `${i*25}%`, right: `${(i%2)*30}%`, whiteSpace: "nowrap", userSelect: "none" }}>{w}</span>
          ))}
        </div>
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg, #8A1613, #95BB83)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem" }}>🎙</div>
            <div>
              <p className="section-label" style={{ color: "#95BB83", marginBottom: 0 }}>Health Plug Radio</p>
              <p style={{ fontFamily: "var(--font)", fontWeight: 800, fontSize: "1.1rem", color: "rgba(255,255,255,0.6)" }}>The Podcast</p>
            </div>
          </div>
          <h1 style={{ fontFamily: "var(--font)", fontWeight: 900, fontSize: "clamp(2.8rem, 7vw, 6rem)", color: "#fff", lineHeight: 0.95, letterSpacing: "-0.03em", marginBottom: "1.25rem" }}>
            The Health Plug<br /><span style={{ color: "#8A1613" }}>Podcast.</span>
          </h1>
          <p style={{ color: "rgba(255,255,255,0.55)", maxWidth: 560, fontFamily: "var(--font-body)", fontSize: "1.05rem", lineHeight: 1.75, marginBottom: "2rem" }}>
            A community-based health edutainment show — a safe, energetic and accessible space for conversations about health, rights, bodies, care and community life.
          </p>
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <Link to="/contact" className="btn-red">Sponsor a Podcast Series</Link>
            <a href="#episodes" className="btn-outline-white" style={{ fontSize: "0.85rem" }}>Browse Episodes</a>
          </div>
        </div>
      </section>

      {/* Episodes */}
      <section id="episodes" style={{ background: "#F7F1DF", padding: "6rem 2rem" }}>
        <div className="container">
          {/* Topic filter */}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "2.5rem" }}>
            {topics.map(t => (
              <button key={t} onClick={() => setFilter(t)} style={{
                background: filter === t ? "#8A1613" : "#fff",
                color: filter === t ? "#fff" : "rgba(11,11,11,0.55)",
                border: `1px solid ${filter === t ? "#8A1613" : "rgba(11,11,11,0.12)"}`,
                borderRadius: 100, padding: "0.4rem 1rem", fontSize: "0.8rem",
                fontFamily: "var(--font)", fontWeight: 600, transition: "all 0.2s",
              }}>{t}</button>
            ))}
          </div>

          {loading && <p style={{ color: "rgba(11,11,11,0.4)", fontFamily: "var(--font-body)" }}>Loading episodes...</p>}

          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {filtered.map(ep => {
              const accentColor = topicColors[ep.topic] || "#8A1613";
              const isPlaying   = playing?.id === ep.id;
              return (
                <div key={ep.id} style={{ background: "#fff", borderRadius: 16, padding: "1.5rem", display: "grid", gridTemplateColumns: "auto 1fr auto", gap: "1.25rem", alignItems: "center", border: `1px solid ${isPlaying ? accentColor+"44" : "rgba(11,11,11,0.07)"}`, transition: "border-color 0.2s, box-shadow 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}>

                  {/* Cover */}
                  <div style={{ width: 76, height: 76, borderRadius: 12, background: `linear-gradient(135deg, ${accentColor}, ${accentColor}88)`, display: "flex", alignItems: "center", justifyContent: "center", cursor: ep.audio_url ? "pointer" : "default", flexShrink: 0, position: "relative", overflow: "hidden" }}
                    onClick={() => ep.audio_url && handlePlay(ep)}>
                    {ep.cover_url && <img src={ep.cover_url} alt={ep.title} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />}
                    <span style={{ fontSize: "1.5rem", position: "relative", zIndex: 1 }}>{isPlaying ? "⏸" : "▶"}</span>
                  </div>

                  {/* Info */}
                  <div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.3rem", flexWrap: "wrap" }}>
                      <span style={{ fontSize: "0.68rem", color: accentColor, fontFamily: "var(--font)", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>{ep.topic}</span>
                      <span style={{ color: "rgba(11,11,11,0.2)", fontSize: "0.7rem" }}>·</span>
                      <span style={{ fontSize: "0.72rem", color: "rgba(11,11,11,0.35)", fontFamily: "var(--font)" }}>S{ep.season}E{ep.episode_number}</span>
                      <span style={{ color: "rgba(11,11,11,0.2)", fontSize: "0.7rem" }}>·</span>
                      <span style={{ fontSize: "0.72rem", color: "rgba(11,11,11,0.35)", fontFamily: "var(--font)" }}>{ep.duration_mins} min</span>
                    </div>
                    <h3 style={{ fontFamily: "var(--font)", fontWeight: 800, fontSize: "1.05rem", color: "#0B0B0B", marginBottom: "0.25rem" }}>{ep.title}</h3>
                    {ep.guest_name && <p style={{ fontSize: "0.82rem", color: "rgba(11,11,11,0.45)", fontFamily: "var(--font-body)", marginBottom: "0.3rem" }}>{ep.guest_name}{ep.guest_title ? ` — ${ep.guest_title}` : ""}</p>}
                    <p style={{ fontSize: "0.82rem", color: "rgba(11,11,11,0.5)", lineHeight: 1.6, fontFamily: "var(--font-body)" }}>{ep.description.substring(0, 110)}...</p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-end", flexShrink: 0 }}>
                    <span style={{ fontSize: "0.72rem", color: "rgba(11,11,11,0.3)", fontFamily: "var(--font)" }}>
                      {new Date(ep.publish_date).toLocaleDateString("en-ZA", { year: "numeric", month: "short", day: "numeric" })}
                    </span>
                    <div style={{ display: "flex", gap: "0.4rem" }}>
                      {ep.spotify_url && <a href={ep.spotify_url} target="_blank" rel="noreferrer" style={{ background: "#1DB954", color: "#fff", borderRadius: 6, padding: "0.35rem 0.8rem", fontSize: "0.72rem", fontWeight: 700, fontFamily: "var(--font)" }}>Spotify</a>}
                      {ep.apple_url   && <a href={ep.apple_url}   target="_blank" rel="noreferrer" style={{ background: "#fc3c44", color: "#fff", borderRadius: 6, padding: "0.35rem 0.8rem", fontSize: "0.72rem", fontWeight: 700, fontFamily: "var(--font)" }}>Apple</a>}
                      {ep.audio_url   && (
                        <button onClick={() => handlePlay(ep)} style={{ background: isPlaying ? accentColor : "#0B0B0B", color: "#fff", border: "none", borderRadius: 6, padding: "0.35rem 0.8rem", fontSize: "0.72rem", fontWeight: 700, fontFamily: "var(--font)", transition: "background 0.2s" }}>
                          {isPlaying ? "⏸ Pause" : "▶ Play"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "4rem 0" }}>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🎙</div>
              <p style={{ color: "rgba(11,11,11,0.4)", fontFamily: "var(--font-body)" }}>No episodes found for this topic yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Sponsor CTA */}
      <section style={{ background: "#8A1613", padding: "5rem 2rem", textAlign: "center" }}>
        <div className="container-sm">
          <h2 style={{ fontFamily: "var(--font)", fontWeight: 900, fontSize: "clamp(1.8rem,4vw,3rem)", color: "#fff", marginBottom: "1rem", letterSpacing: "-0.02em" }}>Sponsor a Podcast Series</h2>
          <p style={{ color: "rgba(255,255,255,0.65)", fontFamily: "var(--font-body)", marginBottom: "2rem", fontSize: "1.05rem" }}>Reach young South Africans through trusted health conversations. Brand your own episode series.</p>
          <Link to="/contact" className="btn-outline-white">Get in Touch</Link>
        </div>
      </section>

      {/* Sticky player */}
      {playing && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(11,11,11,0.97)", borderTop: "1px solid rgba(138,22,19,0.4)", backdropFilter: "blur(12px)", padding: "1rem 2rem", display: "flex", alignItems: "center", gap: "1.5rem", zIndex: 150 }}>
          <div>
            <p style={{ fontSize: "0.65rem", color: "#95BB83", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "var(--font)", fontWeight: 700 }}>Now Playing</p>
            <p style={{ fontFamily: "var(--font)", fontWeight: 700, fontSize: "0.92rem", color: "#fff" }}>{playing.title}</p>
          </div>
          <audio ref={audioRef} controls style={{ flex: 1, height: 36, accentColor: "#8A1613" }}>
            <source src={playing.audio_url} />
          </audio>
          <button onClick={() => setPlaying(null)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: "1.2rem", padding: "0.25rem 0.5rem" }}>✕</button>
        </div>
      )}
    </div>
  );
}
